'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X as XIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import {
  modalOverlayTint,
  modalSurface,
} from '@/shared/ui/shadcn/modal-chrome';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Soft gray dim — Apple-style sheet backdrop, no blur (the blur lives
      // on the modal itself so the underlying UI shows through the panel).
      cn('fixed inset-0 z-50', modalOverlayTint),
      // CSS transition for opacity
      'transition-opacity duration-200 ease-in-out',
      'data-[state=open]:opacity-100 data-[state=closed]:opacity-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    overlayClassName?: string;
    /**
     * Edge-to-edge fullscreen panel (e.g. the image cropper) instead of the
     * default centered-modal / bottom-sheet. Renders a separate, minimal class
     * set — plain `inset-0`, no `sm:` centering, no bottom-sheet anchoring, and
     * crucially none of the `max-sm:!important` width overrides that otherwise
     * outrank a consumer's inline styles. The consumer supplies its own surface
     * (bg/text/layout) via `className`.
     */
    fullScreen?: boolean;
    /**
     * Mobile bottom sheet sizing. `'auto'` (default) sizes to content, capped
     * at 90dvh — the sheet grows upward from the bottom edge as content
     * grows, leaving backdrop above short content. `'tall'` instead sets a
     * floor (min 70dvh, still capped at 88dvh) so the sheet starts near the
     * top even for shorter content, but keeps growing past that floor to fit
     * everything (no clipping) — content scrolls internally only once it
     * exceeds the 88dvh cap. Use for content-heavy mobile sheets (e.g. a
     * paywall with several product cards) where "auto" would otherwise
     * render a short sheet squeezed against the bottom edge with unused
     * backdrop space above it.
     * No effect on desktop (`sm:`), where the dialog is a centered modal.
     */
    mobileSheetHeight?: 'auto' | 'tall';
  }
>(
  (
    {
      className,
      children,
      showCloseButton = true,
      overlayClassName,
      fullScreen = false,
      mobileSheetHeight = 'auto',
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-50',
          fullScreen
            ? // Fullscreen: fill the viewport, no chrome of our own. Only a
              // fade so open/close isn't abrupt; the consumer owns everything
              // visual via `className`.
              cn(
                'inset-0 max-w-none max-h-none overflow-hidden rounded-none p-0',
                'translate-x-0 translate-y-0',
                'transition-opacity duration-200 ease-out',
                'data-[state=open]:opacity-100 data-[state=closed]:opacity-0'
              )
            : cn(
                // Apple-style Liquid Glass chrome (shared mobile + desktop):
                // borderless, translucent surface (~70%) with heavy backdrop
                // blur so the page UI shows through as a softened frosted layer,
                // soft diffuse shadow.
                cn('grid gap-4', modalSurface),
                // MOBILE = bottom sheet (matches the native sheets on
                // Apple/Android). Anchored to the bottom edge, full-bleed,
                // rounded top, scrolls internally, slides up from below. This is
                // the systemic anchor: the old centered modal (top-[20%] +
                // translate-y-[-30%]) pushed its top edge OFF-SCREEN whenever the
                // content was taller than ~⅔ of the viewport (tall paywalls,
                // guides, the Pass card) — what looked like "the sheet renders
                // from the top and clips".
                'inset-x-0 bottom-0 overflow-x-hidden overflow-y-auto rounded-t-3xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]',
                mobileSheetHeight === 'tall'
                  ? 'max-h-[88dvh] min-h-[70dvh]'
                  : 'max-h-[90dvh]',
                'transition-all duration-300 ease-out',
                'data-[state=open]:translate-y-0 data-[state=closed]:translate-y-full',
                'data-[state=open]:opacity-100 data-[state=closed]:opacity-0',
                // Force full-bleed on mobile even when a consumer passes its own
                // width / max-width (those values are meant for the desktop card).
                'max-sm:!inset-x-0 max-sm:!w-auto max-sm:!max-w-none',
                // DESKTOP (sm+) = centered modal: undo the bottom anchoring, fade
                // + scale instead of slide.
                'sm:inset-x-auto sm:bottom-auto sm:left-[50%] sm:top-[50%] sm:max-h-[85vh] sm:rounded-3xl sm:pb-6',
                'sm:translate-x-[-50%] sm:translate-y-[-50%]',
                'sm:data-[state=open]:translate-y-[-50%] sm:data-[state=closed]:translate-y-[-50%]',
                'sm:data-[state=open]:scale-100 sm:data-[state=closed]:scale-95',
                // Desktop sizing defaults — only when the consumer didn't set its own.
                !(className && /(?:^|\s)(?:sm:|md:|lg:|xl:|2xl:)?w-/.test(className)) && 'sm:w-full',
                !className?.includes('max-w-') && 'sm:max-w-lg'
              ),
          className
        )}
        aria-describedby={undefined}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close className="absolute end-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Left-aligned everywhere — centered modal copy reads as a marketing
      // banner; we want the standard Apple sheet-row look.
      'flex flex-col space-y-1.5 text-start',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-row justify-end gap-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-sheet-title leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-form-help text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
